import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { withUpdatedTotals } from 'helpers/budgetTotals';
import { api } from 'services/api';
import history from 'services/history';
import { useSelector } from 'store/selector';
import { Surgerie } from 'types/surgerie';

type ValueField = 'value_doctor' | 'value_hospital' | 'value_anesthetist';

// Só o que a edição grava: serve para saber se há alterações pendentes.
function snapshot(budget: Surgerie | null) {
  if (!budget) return '';

  return JSON.stringify([
    budget.value_doctor,
    budget.value_hospital,
    budget.value_anesthetist,
    budget.packages.map(item => item.package?.sale_price || 0),
  ]);
}

// A API pode devolver os valores como texto; as contas precisam de número.
function normalize(budget: Surgerie): Surgerie {
  return {
    ...budget,
    value_doctor: Number(budget.value_doctor) || 0,
    value_hospital: Number(budget.value_hospital) || 0,
    value_anesthetist: Number(budget.value_anesthetist) || 0,
    value_packages: Number(budget.value_packages) || 0,
    value_budget: Number(budget.value_budget) || 0,
    value_total: Number(budget.value_total) || 0,
    value_markup: Number(budget.value_markup) || 0,
    value_paid: Number(budget.value_paid) || 0,
    packages: (budget.packages || []).map(item => ({
      ...item,
      package: item.package ? { ...item.package, sale_price: Number(item.package.sale_price) || 0 } : null,
    })),
  };
}

export function useSurgerieRecord(id: string | undefined) {
  const user = useSelector(state => state.user);
  const [budget, setBudget] = useState<Surgerie | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    return api
      .get<Surgerie>(`/budgets/${id}`)
      .then(response => {
        const loaded = normalize(response.data);
        setBudget(loaded);
        setSavedSnapshot(snapshot(loaded));
      })
      .catch(() => {
        toast.error('Não foi possível carregar o orçamento');
        history.push('/surgeries/open');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const dirty = useMemo(() => snapshot(budget) !== savedSnapshot, [budget, savedSnapshot]);

  function changeValue(field: ValueField, value: string) {
    setBudget(current => (current ? withUpdatedTotals({ ...current, [field]: parseFloat(value) || 0 }) : null));
  }

  function changePackageValue(index: number, value: string) {
    setBudget(current => {
      if (!current) return null;

      const packages = [...current.packages];
      const item = packages[index];
      packages[index] = {
        ...item,
        package: item.package ? { ...item.package, sale_price: parseFloat(value) || 0 } : null,
      };

      return withUpdatedTotals({ ...current, packages });
    });
  }

  function request(call: () => Promise<unknown>, success: string, failure: string) {
    setSaving(true);

    return call()
      .then(() => {
        toast.success(success);
        return load();
      })
      .catch(err => {
        toast.error(err.response?.data?.error || failure);
      })
      .finally(() => setSaving(false));
  }

  const save = () =>
    budget &&
    request(
      () => api.put(`/budgets/${budget.id_int}`, budget),
      'Orçamento atualizado',
      'Não foi possível salvar o orçamento',
    );

  const close = () =>
    budget &&
    request(
      () => api.put(`/budgets/close/${budget.id_int}`, { ...budget, closed_by: user?.name, status: 'closed' }),
      'Orçamento fechado',
      'Não foi possível fechar o orçamento',
    );

  return { budget, loading, saving, dirty, changeValue, changePackageValue, save, close };
}
